import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ViewportService } from './viewport.service';

const DRAG_THRESHOLD = 80;
const VELOCITY_THRESHOLD = 0.5;

@Injectable({ providedIn: 'root' })
export class MobileDialogDragService {
  private readonly dialog = inject(MatDialog);
  private readonly viewport = inject(ViewportService);

  constructor() {
    if (!this.viewport.isPhone()) return;
    this.dialog.afterOpened.subscribe((ref) => {
      ref.afterOpened().subscribe(() => this.attach(ref));
    });
  }

  private attach(ref: MatDialogRef<unknown>): void {
    const container = document.getElementById(ref.id);
    const pane =
      container?.closest<HTMLElement>('.cdk-overlay-pane') ?? undefined;

    if (!pane) return;
    if (pane.classList.contains('mobile-drawer-panel')) return;
    if (pane.querySelector('.ui-modal--compact')) return;

    let startY = 0;
    let startTime = 0;
    let currentDelta = 0;
    let canDrag = false;

    const snapBack = (): void => {
      pane.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)';
      pane.style.transform = 'translateY(0)';
      setTimeout(() => {
        pane.style.transition = '';
        pane.style.transform = '';
      }, 300);
    };

    const onTouchStart = (e: TouchEvent): void => {
      const headerEl = pane.querySelector<HTMLElement>('.header');
      if (!headerEl?.contains(e.target as Node)) {
        canDrag = false;
        return;
      }
      canDrag = true;
      startY = e.touches[0].clientY;
      startTime = Date.now();
      currentDelta = 0;
    };

    const onTouchMove = (e: TouchEvent): void => {
      if (!canDrag) return;
      const delta = e.touches[0].clientY - startY;
      if (delta <= 0) {
        canDrag = false;
        snapBack();
        return;
      }
      e.preventDefault();
      currentDelta = delta;
      pane.style.transition = 'none';
      pane.style.transform = `translateY(${delta}px)`;
    };

    const onTouchEnd = (): void => {
      if (!canDrag) {
        canDrag = false;
        return;
      }
      const velocity = currentDelta / Math.max(Date.now() - startTime, 1);
      if (currentDelta > DRAG_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
        pane.style.transition = 'transform 220ms ease-in';
        pane.style.transform = 'translateY(100%)';
        setTimeout(() => ref.close(), 220);
      } else {
        snapBack();
      }
      canDrag = false;
      currentDelta = 0;
    };

    pane.addEventListener('touchstart', onTouchStart, { passive: true });
    pane.addEventListener('touchmove', onTouchMove, { passive: false });
    pane.addEventListener('touchend', onTouchEnd);

    ref.afterClosed().subscribe(() => {
      pane.removeEventListener('touchstart', onTouchStart);
      pane.removeEventListener('touchmove', onTouchMove);
      pane.removeEventListener('touchend', onTouchEnd);
    });
  }
}
